import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useCallback, useEffect } from 'react';

import { ERROR_BASE_DATE } from '~/constants/organization_notification';
import {
  GetUnreadAgentNotificationsDocument,
  MarkOrganizationNotificationDocument,
} from '~/graphql/generated/graphql';
import { getMe } from '~/graphql/utility';
import { useSubscriptions } from '~/hooks/use-subscriptions';
import { agentNotificationsState } from '~/state/layouts/SharedAppShell/atoms';

export const useSubscriptionAgentNotifications = () => {
  const [{ notifications, loaded }, setState] = useAtom(agentNotificationsState);
  const [notificationCreatedEvent, clear] = useSubscriptions('OrganizationNotification', [
    'created',
  ]);
  const [markNotification] = useMutation(MarkOrganizationNotificationDocument);
  const [getNotifications, { data, loading }] = useLazyQuery(GetUnreadAgentNotificationsDocument, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (loading) return;
    if (!data) return;

    const notifications = getMe(data)?.organization.unreadOrganizationNotifications.nodes || [];

    setState((_state) => ({
      ..._state,
      notifications,
      loaded: true,
    }));
  }, [data, loading, setState]);

  const handleClose = useCallback(
    async (notificationId: string) => {
      const newNotifications = notifications.filter(
        (notification) => notification.id !== notificationId,
      );
      setState((_state) => ({ ..._state, notifications: newNotifications }));

      const notification = notifications.find((n) => n.id === notificationId);

      if (notification) {
        await markNotification({
          variables: {
            input: {
              organizationNotificationId: notification.id,
            },
          },
        });
      }
    },
    [markNotification, notifications, setState],
  );

  useEffect(() => {
    if (notificationCreatedEvent) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
      clear();
    }
  }, [clear, notificationCreatedEvent, getNotifications]);

  useEffect(() => {
    // ログイン時に通知を取得する
    if (!loaded) {
      getNotifications({ variables: { from: ERROR_BASE_DATE } });
    }
  }, [getNotifications, loaded]);

  return {
    handleClose,
  };
};
